import { useNavigate } from 'react-router-dom'
import { useState, useEffect, useMemo } from 'react'
import { useMainScreen } from '@/hooks/useMainScreen'
import { useOtcStats } from '@/hooks/useOTC'
import { useUnlocks } from '@/hooks/useUnlocks'
import { useOTC } from '@/hooks/useOTC'
import { useIndustries } from '@/hooks/useIndustries'
import { formatCompactNumber } from '@/utils'
import { OrderbookItem } from '@/types/otc'
import { PageTemplate } from '@/components/templates'
import { CONTAINER_SIZES } from '@/constants'
import { Button } from '@/components/ui/button'
import OrderbookTable from '@/components/orderbook/OrderbookTable'
import { OrderInquiryModal } from '@/components/organisms/modals'
import { useProfile } from '@/hooks/useProfile'
import { TrendingAssets, TopDiscountsToday, Categories, UpcomingUnlocks, MarketIndicator, DashboardStats } from '@/components/molecules'
import { formStyles } from '@/constants/styles'

const MainScreen = () => {
  const navigate = useNavigate()
  const { data, loading, error } = useMainScreen()
  const { data: otcStats, loading: otcStatsLoading } = useOtcStats()
  const { profile } = useProfile()
  const { industries, loading: industriesLoading } = useIndustries()
  const [orderInquiryModal, setOrderInquiryModal] = useState(false)
  const [selectedOrder, setSelectedOrder] = useState<OrderbookItem | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string>('')

  // Set first industry as selected when industries are loaded
  useEffect(() => {
    if (industries.length > 0 && !selectedCategory) {
      setSelectedCategory(industries[0])
    }
  }, [industries, selectedCategory])

  // Fetch all orders once (without industry filter) to avoid re-fetching on category change
  const allCategoryOrdersParams = useMemo(() => ({
    limit: 50 // Load enough orders to cover all categories
  }), [])

  const { orders: allCategoryOrders, loading: categoryOrdersLoading } = useOTC(allCategoryOrdersParams)

  // Fetch upcoming unlocks - load more than 4 to enable scrolling, but show only 4 at a time
  const upcomingUnlocksParams = useMemo(() => ({ limit: 20 }), []) // Load more for scrolling
  const { unlocks: upcomingUnlocks, loading: upcomingUnlocksLoading } = useUnlocks(upcomingUnlocksParams)

  // Filter and memoize category items on client side to prevent unnecessary re-renders
  const categoryItems = useMemo(() => {
    const filtered = selectedCategory
      ? allCategoryOrders.filter(order => order.industry === selectedCategory)
      : allCategoryOrders
    
    return filtered.slice(0, 4).map(order => ({
      ...order,
      category: order.industry || selectedCategory
    }))
  }, [allCategoryOrders, selectedCategory])

  const handleOrderClick = (order: OrderbookItem) => {
    setSelectedOrder(order)
    setOrderInquiryModal(true)
  }

  const handleInquirySubmitted = () => {
    // Refresh data or show success message
    if (process.env.NODE_ENV === 'development') {
    console.log('Order inquiry submitted successfully')
    }
  }

  if (loading) {
    return <PageTemplate isLoading />
  }

  if (error) {
    return (
      <PageTemplate>
        <div className="text-center py-8">
          <p className="text-red-500">{error}</p>
          <Button 
            onClick={() => window.location.reload()}
            className="mt-4"
          >
            Try Again
          </Button>
        </div>
      </PageTemplate>
    )
  }

  // Top bar metrics from /orderbook/stats
  // API response: { data: { total_projects, total_orderbook_value, buy_orders, sell_orders, price_spread, deal_spread, ... } }
  // Used for: projects count, orderbook value, buy/sell split, avg. discount, price spread, greed/fear
  const totalProjects = otcStats?.total_projects || 0
  const totalOrderbookValue = otcStats?.total_orderbook_value || 0
  const buyOrders = otcStats?.buy_orders || 0
  const sellOrders = otcStats?.sell_orders || 0
  const priceSpread = otcStats?.price_spread || 0
  const dealSpread = otcStats?.deal_spread || 0
  
  // Average Discount - API provides this as a percentage
  const averageDiscount = otcStats?.assets_on_discount || 0
  
  // Calculate buyer percentage for greed/fear indicator
  // Based on buy_orders vs sell_orders ratio
  const totalOrders = buyOrders + sellOrders
  const buyerPercentage = totalOrders > 0 ? Math.round((buyOrders / totalOrders) * 100) : 50
  const sentiment = buyerPercentage >= 60 ? 'Greed' : buyerPercentage <= 40 ? 'Fear' : 'Neutral'

  return (
    <div 
      className="relative min-h-screen overflow-x-hidden"
      style={{
        background: 'var(--page-gradient)'
      }}
    >
      
      {/* Background ellipses - LIGHT THEME */}
      {/* Primary lime ellipse - top right (light theme) */}
      <div 
        className="absolute pointer-events-none dark:hidden block"
        style={{
          top: '-100px',
          right: '-200px',
          width: '761px',
          height: '761px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(227,253,90,0.5) 0%, rgba(227,253,90,0.25) 30%, rgba(227,253,90,0.08) 50%, transparent 70%)',
          filter: 'blur(80px)',
          zIndex: 0
        }}
      />
      {/* Secondary bluish ellipse - left side (light theme) */}
      <div 
        className="absolute pointer-events-none dark:hidden block"
        style={{
          top: '281px',
          left: '-200px',
          width: '985px',
          height: '985px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(180,190,220,0.3) 0%, rgba(180,190,220,0.15) 40%, transparent 70%)',
          filter: 'blur(100px)',
          zIndex: 0
        }}
      />
      
      <PageTemplate
        maxWidth={CONTAINER_SIZES.WIDE}
      >
        <div className="space-y-4 md:space-y-6 relative" style={{ zIndex: 10 }}>
        {/* Dashboard Stats Section */}
        <DashboardStats
          profile={profile || undefined}
          totalProjects={totalProjects}
          totalOrderbookValue={totalOrderbookValue}
          averageDiscount={averageDiscount}
          buyOrders={buyOrders}
          sellOrders={sellOrders}
              loading={otcStatsLoading}
          totalProjectsMonthlyAvg={otcStats?.total_projects_monthly_avg}
          orderbookMonthlyAvg={otcStats?.orderbook_monthly_avg}
                />

        {/* Trending Assets Section */}
        <TrendingAssets 
          assets={data?.trendingAssets.slice(0, 15) || []}
          onAssetClick={handleOrderClick}
          loading={loading}
        />

        {/* Dashboard Blocks Section - All 4 blocks with equal height */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
          {/* Block 1: Top Discounts Today */}
          <div className="lg:col-span-2 flex h-full">
            <div className={`${formStyles.cardNoShadow} flex flex-col w-full h-full`}>
              <TopDiscountsToday
                loading={loading}
                items={data?.discountedAssets
                  ?.sort((a, b) => Math.abs(b.discount || 0) - Math.abs(a.discount || 0))
                  ?.map((asset) => {
                  // API returns discount as negative value (e.g., -30.5 means 30.5% discount)
                  // Convert to positive for display
                  const discountPercent = Math.abs(asset.discount || 0)
                  
                  // Determine tag based on discount percentage
                  let tag: 'hot' | 'trending' | 'most_profitable' = 'trending'
                  if (discountPercent >= 30) {
                    tag = 'hot'
                  } else if (discountPercent >= 25) {
                    tag = 'most_profitable'
                  } else {
                    tag = 'trending'
                  }
                  
                  return {
                    ...asset,
                    discount: discountPercent, // Convert to positive
                    tag,
                    // priceHistory will be generated by PriceChart component if not provided
                  }
                }) || []}
                onItemClick={(item) => {
                  if (item.project_id) {
                    navigate(`/projects/${item.project_id}`)
                  }
                }}
                onBuyClick={handleOrderClick}
              />
            </div>
          </div>

          {/* Block 2: Categories */}
          <div className="lg:col-span-1 flex h-full">
            <div className={`${formStyles.cardNoShadow} flex flex-col w-full h-full`}>
              <Categories
                items={categoryItems}
                categories={industries}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                onItemClick={handleOrderClick}
                loading={industriesLoading || categoryOrdersLoading}
              />
            </div>
          </div>

          {/* Block 3: Upcoming Unlocks */}
          <div className="lg:col-span-1 flex h-full">
            <div className={`${formStyles.cardNoShadow} flex flex-col w-full h-full`}>
              <UpcomingUnlocks
                items={upcomingUnlocks.map((unlock, index) => {
                  // Calculate days until next unlock
                  const days = unlock.next_unlock_date 
                    ? Math.ceil((new Date(unlock.next_unlock_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
                    : 0
                  
                  // Calculate percentage of next unlock
                  const percentage = unlock.total_supply > 0 && unlock.next_unlock_tokens
                    ? (unlock.next_unlock_tokens / unlock.total_supply) * 100
                    : 0
                  
                  // Format value (simplified - would need price data for actual value)
                  const value = unlock.next_unlock_tokens 
                    ? `$${formatCompactNumber(unlock.next_unlock_tokens)}`
                    : '$0'
                  
                  const colors = ['bg-green-500', 'bg-teal-500', 'bg-yellow-500', 'bg-pink-500', 'bg-purple-500', 'bg-blue-500']
                  
                  return {
                    id: unlock.project_id,
                    name: unlock.project_name,
                    days: Math.max(0, days),
                    value,
                    percentage: Math.round(percentage * 100) / 100,
                    color: colors[index % colors.length],
                    logo: unlock.logo
                  }
                })}
                onItemClick={(item) => {
                  const unlock = upcomingUnlocks.find(u => u.project_id === item.id)
                  if (unlock) {
                    navigate(`/projects/${unlock.project_id}`)
                  }
                }}
                onDetailsClick={(item) => {
                  const unlock = upcomingUnlocks.find(u => u.project_id === item.id)
                  if (unlock) {
                    navigate(`/projects/${unlock.project_id}/unlock`)
                  }
                }}
                loading={upcomingUnlocksLoading}
              />
            </div>
          </div>

          {/* Block 4: Market Indicator */}
          <div className="lg:col-span-2 flex h-full">
            <div className={`${formStyles.cardNoShadow} flex flex-col w-full h-full`}>
              <MarketIndicator
                buyerPercentage={buyerPercentage}
                sentiment={sentiment}
                priceSpread={priceSpread}
                dealSpread={dealSpread}
                loading={otcStatsLoading}
              />
            </div>
          </div>
        </div>

      {/* Orders Table Section */}
      <OrderbookTable
        title="OTC Orders"
        navigate={navigate}
        useDataHook={useOTC}
        dataKey="orders"
        showPagination={true}
        showViewOTCMarket={true}
        useUrlParams={true}
        showOrderInquiry={true}
      />
      </div>

      {/* Order Inquiry Modal */}
      <OrderInquiryModal
        isOpen={orderInquiryModal}
        onClose={() => {
          setOrderInquiryModal(false)
          setSelectedOrder(null)
        }}
        order={selectedOrder}
        onInquirySubmitted={handleInquirySubmitted}
      />
      </PageTemplate>
    </div>
  )
}

export default MainScreen 